// src/pages/Upload.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload as UploadIcon, FileText, X, CheckCircle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Sidebar from '../components/Sidebar';
import Toast from '../components/Toast';
import { supabase } from '../utils/supabaseClient';

const API_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api';

const Upload = ({ darkMode, toggleDarkMode }) => {
  const navigate = useNavigate();
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadComplete, setUploadComplete] = useState(false);
  const [toast, setToast] = useState(null);

  // --------------------- helpers ---------------------
  const showToast = (msg, type = 'info') => {
    setToast({ message: msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (['dragenter', 'dragover'].includes(e.type)) setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) handleFile(e.dataTransfer.files[0]);
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]);
  };

  const handleFile = (file) => {
    if (file.type !== 'application/pdf') {
      showToast('Please upload a PDF file', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      showToast('File size must be less than 10 MB', 'error');
      return;
    }
    setSelectedFile(file);
  };

  // --------------------- upload logic ---------------------
  const handleUpload = async () => {
    if (!selectedFile) {
      showToast('Please select a file first', 'error');
      return;
    }

    try {
      // get current supabase user id
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id;
      if (!userId) {
        showToast('Please log in first.', 'error');
        return;
      }

      setUploading(true);
      setUploadProgress(10);

      // build FormData for backend
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('user_id', userId);

      const response = await fetch(`${API_URL}/upload-paper-bg`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        showToast(data.detail || 'Upload failed', 'error');
        setUploading(false);
        return;
      }

      setUploadProgress(100);
      setUploading(false);
      setUploadComplete(true);
      showToast('Paper uploaded successfully!', 'success');
      console.log('Backend response:', data);
    } catch (err) {
      console.error(err);
      showToast('Something went wrong while uploading.', 'error');
      setUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setUploadProgress(0);
    setUploadComplete(false);
  };

  const handleViewResults = () => navigate('/dashboard');

  // --------------------- UI ---------------------
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      <Navbar darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <div className="flex">
        <Sidebar />

        <main className="flex-1 p-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Upload Research Paper
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Upload a PDF research paper to analyze and generate insights
            </p>
          </div>

          {/* Upload Section */}
          <div className="max-w-3xl mx-auto">
            {!uploadComplete ? (
              <div className="card">
                {/* Drop Zone */}
                <div
                  className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                    dragActive
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900'
                      : 'border-gray-300 dark:border-gray-600'
                  }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <UploadIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                    Drop your PDF here
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">or click to browse</p>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={handleChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload" className="btn-primary cursor-pointer inline-block">
                    Select PDF File
                  </label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-4">
                    Maximum file size: 10 MB
                  </p>
                </div>

                {/* Selected File + Progress */}
                {selectedFile && (
                  <div className="mt-6 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-8 h-8 text-primary-600" />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">
                            {selectedFile.name}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleRemoveFile}
                        className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900 rounded-lg transition-colors"
                      >
                        <X className="w-5 h-5" />
                      </button>
                    </div>

                    {uploading && (
                      <div className="mt-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm text-gray-600 dark:text-gray-400">
                            Uploading...
                          </span>
                          <span className="text-sm font-medium text-primary-600">
                            {uploadProgress}%
                          </span>
                        </div>
                        <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Upload button */}
                {selectedFile && !uploading && (
                  <div className="mt-6">
                    <button
                      onClick={handleUpload}
                      className="w-full btn-primary flex items-center justify-center space-x-2"
                    >
                      <UploadIcon className="w-5 h-5" />
                      <span>Upload and Analyze</span>
                    </button>
                  </div>
                )}

                {/* Info Box */}
                <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
                    What happens next?
                  </h4>
                  <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
                    <li>• Paper is uploaded to backend</li>
                    <li>• Backend stores it in Supabase & triggers AI analysis</li>
                    <li>• You can track results from Dashboard</li>
                  </ul>
                </div>
              </div>
            ) : (
              // Success Message
              <div className="card text-center">
                <CheckCircle className="w-20 h-20 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Upload Complete!
                </h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Your paper is being processed. You can view progress in the dashboard.
                </p>
                <button onClick={handleViewResults} className="btn-primary">
                  Go to Dashboard
                </button>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Upload;